/**
 * planCalendar — mapea la rutina semanal del plan a fechas reales del calendario.
 *
 * El plan tiene una fecha de inicio y N semanas de rutina (cada semana con días
 * Lunes..Domingo). A partir del inicio, cada fecha del calendario "cae" en una
 * semana del programa y en un día de la semana → mostramos la rutina de ese día
 * (o Descanso si ese weekday no tiene rutina). Ej: inicio jueves 23/may →
 * el 23 muestra la rutina del Jueves.
 */
import type { RoutineDay, RoutineWeek } from "../types/plan";

/** getDay() de JS (0=Dom..6=Sáb) → label en español del catálogo de rutina. */
const GETDAY_TO_LABEL = [
  "Domingo", "Lunes", "Martes", "Miércoles", "Jueves", "Viernes", "Sábado",
];

function norm(s: string): string {
  return (s || "")
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .trim();
}

/** Parsea una fecha tolerando formatos: YYYY-MM-DD, DD/MM/YYYY, DD-MM-YYYY,
 *  "23 de mayo", etc. Devuelve un Date a medianoche local, o null. */
export function parsePlanDate(s: string | null | undefined): Date | null {
  if (!s) return null;
  const str = String(s).trim();
  // ISO YYYY-MM-DD
  let m = str.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (m) return atMidnight(new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])));
  // DD/MM/YYYY o DD-MM-YYYY
  m = str.match(/^(\d{1,2})[/-](\d{1,2})[/-](\d{2,4})/);
  if (m) {
    const yr = Number(m[3].length === 2 ? "20" + m[3] : m[3]);
    return atMidnight(new Date(yr, Number(m[2]) - 1, Number(m[1])));
  }
  const d = new Date(str);
  return Number.isNaN(d.getTime()) ? null : atMidnight(d);
}

function atMidnight(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

function daysBetween(a: Date, b: Date): number {
  return Math.round((atMidnight(b).getTime() - atMidnight(a).getTime()) / 86_400_000);
}

export function addDays(d: Date, n: number): Date {
  const r = atMidnight(d);
  r.setDate(r.getDate() + n);
  return r;
}

/** Fin del plan = inicio + (semanas × 7) − 1. null si no hay inicio/semanas. */
export function planEndDate(start: Date | null, weeksCount: number): Date | null {
  if (!start || weeksCount <= 0) return null;
  return addDays(start, weeksCount * 7 - 1);
}

export interface DateRoutine {
  inPlan: boolean;
  weekIndex: number;       // -1 si fuera del plan
  day: RoutineDay | null;  // el día de rutina que cae en esa fecha
  isRest: boolean;         // true si ese weekday no tiene entrenamiento
}

/** Qué rutina cae en `date` dado el inicio + las semanas del plan.
 *  - Si se pasa `end` (fin del plan explícito del coach), el plan corre hasta
 *    ESA fecha: si el rango es más largo que las semanas de rutina, la rutina
 *    se REPITE (cicla) para llenar todo el período.
 *  - Sin `end`, el plan dura exactamente `weeks.length` semanas (comportamiento
 *    legacy: el calendario se derivaba del nº de semanas). */
export function routineForDate(
  date: Date,
  start: Date | null,
  weeks: RoutineWeek[],
  end: Date | null = null,
): DateRoutine {
  const notInPlan = { inPlan: false, weekIndex: -1, day: null, isRest: false };
  if (!start || weeks.length === 0) return notInPlan;
  const diff = daysBetween(start, date);
  if (diff < 0) return notInPlan;
  if (end && daysBetween(date, end) < 0) return notInPlan; // pasó el fin
  const elapsedWeek = Math.floor(diff / 7);
  // Sin fin explícito, el plan termina cuando se acaban las semanas de rutina.
  if (!end && elapsedWeek >= weeks.length) return notInPlan;
  const week = weeks[elapsedWeek % weeks.length]; // cicla si el rango es más largo
  const wantedLabel = norm(GETDAY_TO_LABEL[date.getDay()]);
  const day = (week.days || []).find((d) => norm(d.label) === wantedLabel) || null;
  const isRest = !day || !day.exercises || day.exercises.length === 0;
  return { inPlan: true, weekIndex: elapsedWeek, day, isRest };
}

export interface CalendarCell {
  date: Date | null;       // null = relleno para alinear el grid
  inPlan: boolean;
  isToday: boolean;
  weekIndex: number;
  focus: string;           // grupo/foco del día, "" si descanso
  isRest: boolean;
}

export interface CalendarMonth {
  year: number;
  month: number;           // 0-11
  label: string;           // "Mayo 2026"
  cells: CalendarCell[];   // múltiplo de 7, semana empieza Lunes
}

const MONTH_NAMES = [
  "Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio",
  "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre",
];

/** getDay() (0=Dom) → índice con semana que empieza en Lunes (Lun=0..Dom=6). */
function mondayIndex(d: Date): number {
  return (d.getDay() + 6) % 7;
}

/** Construye los meses del calendario que cubren [start, end], con celdas por
 *  día (semana Lunes-primero) y la rutina/foco de cada día dentro del plan. */
export function buildCalendarMonths(
  start: Date | null,
  weeks: RoutineWeek[],
  today: Date = new Date(),
  explicitEnd: Date | null = null,
): CalendarMonth[] {
  // Si el coach puso fecha de fin, el calendario llega HASTA esa fecha (no la
  // derivada del nº de semanas). Si no, cae al fin derivado de las semanas.
  const end = explicitEnd || planEndDate(start, weeks.length);
  if (!start || !end) return [];
  const todayMid = atMidnight(today);
  const months: CalendarMonth[] = [];
  let cursor = new Date(start.getFullYear(), start.getMonth(), 1);
  const lastMonth = new Date(end.getFullYear(), end.getMonth(), 1);

  while (cursor <= lastMonth) {
    const year = cursor.getFullYear();
    const month = cursor.getMonth();
    const firstDay = new Date(year, month, 1);
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const cells: CalendarCell[] = [];
    // Relleno inicial hasta el primer día (semana empieza Lunes)
    for (let i = 0; i < mondayIndex(firstDay); i++) {
      cells.push(emptyCell());
    }
    for (let dayNum = 1; dayNum <= daysInMonth; dayNum++) {
      const date = new Date(year, month, dayNum);
      const r = routineForDate(date, start, weeks, end);
      cells.push({
        date,
        inPlan: r.inPlan,
        isToday: date.getTime() === todayMid.getTime(),
        weekIndex: r.weekIndex,
        focus: r.day?.focus || "",
        isRest: r.inPlan && r.isRest,
      });
    }
    // Relleno final hasta completar la última semana
    while (cells.length % 7 !== 0) cells.push(emptyCell());
    months.push({ year, month, label: `${MONTH_NAMES[month]} ${year}`, cells });
    cursor = new Date(year, month + 1, 1);
  }
  return months;
}

function emptyCell(): CalendarCell {
  return { date: null, inPlan: false, isToday: false, weekIndex: -1, focus: "", isRest: false };
}

export const WEEKDAY_HEADERS = ["Lun", "Mar", "Mié", "Jue", "Vie", "Sáb", "Dom"];

export function formatPlanDate(d: Date | null): string {
  if (!d) return "—";
  return `${d.getDate()} ${MONTH_NAMES[d.getMonth()].toLowerCase().slice(0, 3)} ${d.getFullYear()}`;
}
